import { useEffect, useRef, useState } from 'react';

import { ReportType } from '@kaiizen/kai-reports';

import { type AnalyticsEventName, trackEvent } from '@/Client/analytics';
import { triggerDownload } from '@/Client/Lib/downloadLib';
import { logError } from '@/Client/Lib/logger';
import { useToast } from '../../State/ToastContext';

/**
 * Common return shape of the scan-export hooks (useScanPdfExport,
 * useScanExcelExport). Both wrap useCachedAsyncExport, so their result types
 * are interchangeable here.
 */
type ScanExportResult = {
  reportUrl: string | undefined;
  prepareReport: () => void;
  isLoading: boolean;
  isError: boolean;
  error?: Error | undefined;
};

type ScanExportHook = (scanId: string | undefined, reportType: ReportType) => ScanExportResult;

/**
 * Local helper hook for ScopePrintPreview. Wires a scan-export hook (PDF or
 * Excel) into a one-shot "click → download → toast" UX:
 *
 *  - Returns prepareReport so the consumer can hook it to a button.
 *  - While the export job is running, shows a "your report will download
 *    when it's ready" toast.
 *  - When the export job's URL changes (the user-triggered "fresh URL"
 *    signal — see useCachedAsyncExport's `enabled` gate), downloads the
 *    file. Each fresh URL produces exactly one download.
 *  - On error, surfaces an error toast.
 *
 * Kept separate from useCachedAsyncExport so the cache hook can be reused
 * elsewhere without dragging in toasts or this component's download policy.
 */
export const useScopeReportDownloader = ({
  exportHook,
  scanId,
  reportType,
  kind,
  eventName,
}: {
  /** The format-specific export hook (useScanPdfExport, useScanExcelExport). */
  exportHook: ScanExportHook;
  scanId: string | undefined;
  reportType: ReportType;
  /** Human label for toasts ("PDF", "Excel"). */
  kind: string;
  eventName: AnalyticsEventName;
}) => {
  const [reportRequestCount, setReportRequestCount] = useState(0);
  const { reportUrl, prepareReport, isLoading, isError, error } = exportHook(scanId, reportType);
  const { showToast, showErrorToast } = useToast();
  const lastReportRequestDownloadCount = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!isError) return;
    logError(`Failed to export ${kind}`, error);
    showErrorToast(error, `export ${kind}`);
  }, [isError, error, kind, showErrorToast]);

  useEffect(() => {
    if (!isLoading) return;
    showToast(`Your ${kind} report will download automatically when it's ready. Do not leave this page.`);
  }, [isLoading, kind, showToast]);

  useEffect(() => {
    if (reportUrl && reportRequestCount > 0) {
      if (reportRequestCount === lastReportRequestDownloadCount.current) return;
      lastReportRequestDownloadCount.current = reportRequestCount;
      console.log('downloading report (count=' + reportRequestCount + ') ' + kind, reportUrl);
      triggerDownload(reportUrl);
      trackEvent(eventName, { scanId, reportType });
    }
    // Intentionally only react to URL changes — including kind retriggers
    // downloads on unrelated re-renders. The server provides the filename
    // via Content-Disposition, so no other inputs are needed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportUrl, reportRequestCount]);

  return {
    prepareReport: () => {
      console.log('prepareReport ' + kind);
      setReportRequestCount(reportRequestCount + 1);
      prepareReport();
    },
    isLoading,
  };
};
